import { useI18n } from '../../i18n';
// ============================================
// PromotionsPage
// ============================================

import { useState, useMemo, useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { PageHeader, ActionButton, PageContainer, ExportButton } from '../../components/shared';
import { PlusIcon } from '../../components/icons';
import {
  usePromotions,
  usePromotion,
  useCreatePromotion,
  useUpdatePromotion,
  useDeletePromotion,
  useExport,
  useDebounce,
} from '../../hooks';
import { useStoreSelectionStore } from '../../stores';
import { useMoney } from '../../providers/CurrencyProvider';
import type { PromotionStatus } from '@conai/shared-types';

import { PromotionStatsRow } from './components/PromotionStatsRow';
import { PromotionFilterBar } from './components/PromotionFilterBar';
import { PromotionTable, type PromotionTableItem } from './components/PromotionTable';
import { PromotionFormModal, type PromotionFormData, emptyPromotionForm } from './components/PromotionFormModal';
import { PromotionDeleteModal } from './components/PromotionDeleteModal';
import { PromotionDetailModal } from './components/PromotionDetailModal';
import { PromotionTesterModal } from './components/PromotionTesterModal';

// ---- types ----

/**
 * Bug #150: điều kiện áp dụng của promotion (cột `promotions.conditions` JSONB).
 *
 * Bản sao tại chỗ của `PromotionConditions` trong `@conai/shared-types` — khai
 * lại ở đây để màn này không phải chờ `packages/*` build lại mới type-check
 * được. Khi các package đã build, đổi sang `import type { PromotionConditions }`
 * và xoá alias này. Phần CHỜ BACKEND (tên endpoint + tên field) nằm ở
 * `packages/shared-types/src/promotion.types.ts`.
 */
type PromotionConditions = Record<string, string | number | boolean | null>;

type PromotionRaw = {
  id: string;
  code?: string;
  name?: string;
  type?: string;
  discount_type?: string;
  value?: string | number;
  discount_value?: string | number;
  // BE chốt 19/08/2026: chỉ có `start_at`/`end_at`. `start_date`/`end_date`
  // không tồn tại ở serializer promotion nào — chúng là query param của
  // /orders/, /reports/, /receipts/, /audit/. Nhánh đọc hai tên đã gỡ.
  start_at?: string;
  end_at?: string;
  status?: string;
  /** Bug #140: số điểm khách đổi để lấy voucher. Xem PromotionFormModal. */
  points_cost?: number | string | null;
  /**
   * Trần tổng số lượt dùng. `null` = không giới hạn.
   *
   * ⚠️ `total_quantity`, KHÔNG phải `usage_limit` — không serializer promotion
   * nào có field tên `usage_limit` (BE xác nhận 18/09/2026). Bản trước đọc và
   * gửi bằng tên sai, mà DRF bỏ qua field lạ trong im lặng, nên giá trị chưa
   * bao giờ được lưu: đó là bug #251.
   */
  total_quantity?: number | string | null;
  /**
   * Số lượt ĐÃ dùng — BE đếm, FE chỉ đọc. Không có ô nhập nào cho field này.
   *
   * CHƯA được expose (18/09/2026), hiện phải đếm từ `PromotionUsage`. Thiếu
   * field nghĩa là "không biết", không phải "bằng 0".
   */
  usage_count?: number | string | null;
  /**
   * Bug #150: điều kiện áp dụng. Cột `promotions.conditions` là JSONB nên giá
   * trị đọc về là object; vẫn nhận chuỗi phòng khi serializer trả JSON dạng
   * chuỗi. Xem `PromotionConditions` trong `@conai/shared-types` để biết phần
   * còn CHỜ BACKEND.
   */
  conditions?: PromotionConditions | string | null;
};

type ListEnvelope<T> = {
  results?: T[];
  items?: T[];
  data?: T[] | { results?: T[]; items?: T[]; data?: T[] };
  count?: number;
  total?: number;
  meta?: { count?: number };
};

type ModalMode = 'view' | 'create' | 'edit' | 'delete' | null;

// ---- helpers ----
function extractList<T>(payload: unknown): T[] {
  if (Array.isArray(payload)) return payload as T[];
  const d = payload as ListEnvelope<T> | undefined;
  if (!d) return [];
  if (Array.isArray(d.results)) return d.results;
  if (Array.isArray(d.items)) return d.items;
  if (Array.isArray(d.data)) return d.data;
  if (d.data && typeof d.data === 'object') {
    const nested = d.data as ListEnvelope<T>;
    return nested.results ?? nested.items ?? (nested.data as T[]) ?? [];
  }
  return [];
}

function extractCount<T>(payload: unknown, fallback: number): number {
  if (Array.isArray(payload)) return payload.length;
  const d = payload as ListEnvelope<T> | undefined;
  return d?.count ?? d?.total ?? d?.meta?.count ?? fallback;
}

function formatDate(iso: string, locale: string = 'en-US'): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const resolved = locale === 'pl' ? 'pl-PL' : locale === 'uk' ? 'uk-UA' : 'en-US';
  return d.toLocaleDateString(resolved, { month: 'short', day: 'numeric', year: 'numeric' });
}

function normalizeStatus(raw: string): PromotionStatus {
  const s = raw.toUpperCase();
  if (s === 'ACTIVE' || s === 'DRAFT' || s === 'INACTIVE' || s === 'EXPIRED') {
    return s as PromotionStatus;
  }
  return 'DRAFT';
}

/**
 * Trạng thái hiệu lực: BE không tự chuyển ACTIVE -> EXPIRED khi quá end_date,
 * nên FE suy diễn — promotion ACTIVE mà đã qua ngày kết thúc thì coi là EXPIRED
 * (bug #110: hết hạn vẫn hiện Active, thẻ Expired đếm 0).
 */
function effectiveStatus(raw: PromotionRaw): PromotionStatus {
  const status = normalizeStatus(String(raw.status ?? 'DRAFT'));
  if (status !== 'ACTIVE') return status;
  const endIso = String(raw.end_at ?? '');
  if (!endIso) return status;
  const end = new Date(endIso);
  if (Number.isNaN(end.getTime())) return status;
  return end.getTime() < Date.now() ? 'EXPIRED' : status;
}

function resolveDiscountType(raw: PromotionRaw): 'PERCENTAGE' | 'FIXED_AMOUNT' {
  const t = String(raw.type ?? raw.discount_type ?? '').toUpperCase();
  if (t === 'PERCENTAGE' || t === 'PERCENT' || t === 'PERCENTAGE_DISCOUNT') return 'PERCENTAGE';
  return 'FIXED_AMOUNT';
}

function resolveDiscountValue(raw: PromotionRaw): string {
  return String(raw.value ?? raw.discount_value ?? '');
}

/** Formatter tiền của phạm vi đang xem, do component truyền xuống (hook không gọi được ở cấp module). */
type MoneyFormatter = ReturnType<typeof useMoney>;

function mapToTableItem(
  raw: PromotionRaw,
  locale: string = 'en-US',
  money: MoneyFormatter
): PromotionTableItem {
  const discountType = resolveDiscountType(raw);
  const discountValue = resolveDiscountValue(raw);

  let discount: string;
  if (discountType === 'PERCENTAGE') {
    const num = parseFloat(discountValue);
    discount = Number.isFinite(num) ? `${num % 1 === 0 ? num : num}%` : `${discountValue}%`;
  } else {
    // Bug #129: bảng hiện "VND" trong khi form nhập lại gắn nhãn "$" — cùng một
    // con số, hai đơn vị. Cả hai giờ đi qua utils/currency.
    const num = parseFloat(discountValue);
    discount = Number.isFinite(num) ? money.format(num) : `${money.symbol}${discountValue}`;
  }

  const start = formatDate(String(raw.start_at ?? ''), locale);
  const end = formatDate(String(raw.end_at ?? ''), locale);
  const period = start && end ? `${start} - ${end}` : start || end || '-';

  return {
    id: String(raw.id),
    code: String(raw.code ?? `PROMO-${String(raw.id).slice(0, 8)}`),
    name: String(raw.name ?? 'Unnamed'),
    discount,
    discountType,
    period,
    status: effectiveStatus(raw),
  };
}

/**
 * ISO (UTC) -> chuỗi cho `<input type="datetime-local">`.
 *
 * Bug #132: bản cũ dùng `d.toISOString().slice(0,16)` — tức đổ GIỜ UTC vào một ô
 * mà trình duyệt luôn đọc là GIỜ ĐỊA PHƯƠNG. Lúc lưu, `handleFormSubmit` làm
 * `new Date(value).toISOString()` (đọc như giờ địa phương) nên mỗi lần bấm Save
 * ngày bị dịch đúng một lần lệch múi giờ, kể cả khi người dùng không hề chạm vào
 * ô ngày — chỉ đổi trạng thái promotion cũng đủ làm ngày kết thúc trôi đi.
 * Ở UTC+3 (Kyiv), promotion hết hạn lúc 00:30 lùi sang NGÀY HÔM TRƯỚC ngay lần
 * lưu đầu tiên. Đổi sang đọc/ghi thuần giờ địa phương thì vòng lặp
 * hiển thị -> sửa -> lưu -> hiển thị khép kín, không còn dịch.
 */
function toDateInput(iso: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  // datetime-local format: YYYY-MM-DDTHH:mm (giờ địa phương)
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function toFormData(raw: PromotionRaw): PromotionFormData {
  const rawStatus = String(raw.status ?? '').toUpperCase();
  const status: PromotionFormData['status'] =
    ['DRAFT', 'ACTIVE', 'INACTIVE', 'EXPIRED'].includes(rawStatus)
      ? (rawStatus as PromotionFormData['status'])
      : 'DRAFT';

  return {
    name: String(raw.name ?? ''),
    code: String(raw.code ?? ''),
    type: resolveDiscountType(raw),
    value: String(resolveDiscountValue(raw)),
    // Bug #150: object -> chuỗi JSON để ô nhập một dòng hiển thị được, và để
    // lần Save sau `JSON.parse` lại ra đúng object cũ (round-trip khép kín).
    conditions: typeof raw.conditions === 'object' && raw.conditions !== null
      ? JSON.stringify(raw.conditions)
      : String(raw.conditions ?? ''),
    start_at: toDateInput(String(raw.start_at ?? '')),
    end_at: toDateInput(String(raw.end_at ?? '')),
    status,
    // Bug #140: `null` (không dùng điểm) và `0` là hai chuyện khác nhau — chỉ
    // `null`/`undefined` mới thành ô trống.
    points_cost: raw.points_cost == null ? '' : String(raw.points_cost),
    // Cùng quy ước với `points_cost`: `null` = không giới hạn -> ô trống.
    total_quantity: raw.total_quantity == null ? '' : String(raw.total_quantity),
    // Bug #131: mỗi serializer trả phạm vi áp dụng một kiểu — mảng id phẳng
    // (product_ids), mảng object ({id,name}) hoặc lồng trong applicable_to.
    // Đọc thiếu dạng nào là mở form sửa ra thấy 3 mục trống dù promotion có scope.
    product_ids: toTagOptions(raw, ['product_ids', 'products']),
    category_ids: toTagOptions(raw, ['category_ids', 'categories']),
    store_ids: toTagOptions(raw, ['store_ids', 'stores']),
  };
}

/** Chuẩn hoá danh sách phạm vi áp dụng của promotion về TagOption[]. */
function toTagOptions(raw: PromotionRaw, keys: string[]): { id: string; label: string }[] {
  const record = raw as unknown as Record<string, unknown>;
  const applicableTo = (record.applicable_to && typeof record.applicable_to === 'object'
    ? (record.applicable_to as Record<string, unknown>)
    : null);

  for (const key of keys) {
    const source = record[key] ?? applicableTo?.[key];
    if (!Array.isArray(source) || source.length === 0) continue;

    return source
      .map((entry) => {
        if (typeof entry === 'string') return { id: entry, label: entry };
        if (entry && typeof entry === 'object') {
          const obj = entry as Record<string, unknown>;
          const id = String(obj.id ?? obj.uuid ?? '');
          const label = String(obj.name ?? obj.label ?? id);
          return id ? { id, label } : null;
        }
        return null;
      })
      .filter((item): item is { id: string; label: string } => item !== null);
  }

  return [];
}

export function PromotionsPage() {
  const { t, language } = useI18n();
  const money = useMoney();
  const [currentPage, setCurrentPage] = useState(1);
  const [modalMode, setModalMode] = useState<ModalMode>(null);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearch = useDebounce(searchQuery, 500);
  const [statusFilter, setStatusFilter] = useState('');
  const [searchParams, setSearchParams] = useSearchParams();
  const pageSize = 10;

  // Auto-open Detail Modal if 'id' is in URL query parameters
  useEffect(() => {
    const promoId = searchParams.get('id');
    if (promoId) {
      setActiveId(promoId);
      setModalMode('view');
      // Clean query parameters so modal can be closed normally
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('id');
      setSearchParams(newParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const { selectedStoreId } = useStoreSelectionStore();

  // Màn thử khuyến mãi (sheet 2 #41). Danh sách cửa hàng/sản phẩm nạp BÊN TRONG
  // modal chứ không ở đây: modal chỉ mount khi mở, nên hai request đó không
  // chạy với mọi lượt vào trang.
  const [showTester, setShowTester] = useState(false);

  // Store filter do SERVER lọc (BE đã sửa /promotions/?store= — trả promotion
  // gắn store + promotion global). page_size lớn chỉ còn phục vụ search/status
  // client-side, không còn là workaround lọc store nữa.
  const { data: promotionsData, isLoading, error } = usePromotions({
    page: 1,
    page_size: 1000,
    ...(selectedStoreId ? { store: selectedStoreId } : {}),
  } as any);

  const createPromotion = useCreatePromotion();
  const updatePromotion = useUpdatePromotion();
  const deletePromotion = useDeletePromotion();

  const rawList = useMemo(() => extractList<PromotionRaw>(promotionsData), [promotionsData]);

  // #84 (đã đóng): workaround lọc store client-side theo store_ids đã gỡ —
  // server lọc qua param ?store= rồi, kết quả trả về là danh sách cuối cùng.
  const tableData: PromotionTableItem[] = useMemo(
    () => rawList.map(item => mapToTableItem(item, language, money)),
    [rawList, language, money]
  );

  const matchedData = useMemo(() => {
    return tableData.filter((item) => {
      const matchSearch =
        !debouncedSearch ||
        item.code.toLowerCase().includes(debouncedSearch.toLowerCase()) ||
        item.name.toLowerCase().includes(debouncedSearch.toLowerCase());
      const matchStatus = !statusFilter || item.status === statusFilter;
      return matchSearch && matchStatus;
    });
  }, [tableData, debouncedSearch, statusFilter]);

  const totalItems = matchedData.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));

  // Reset to page 1 whenever the effective result set changes.
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedStoreId, debouncedSearch, statusFilter]);

  // Client-side pagination (the whole list is loaded above).
  const filteredData = useMemo(
    () => matchedData.slice((currentPage - 1) * pageSize, currentPage * pageSize),
    [matchedData, currentPage, pageSize]
  );

  const stats = useMemo(() => {
    const all = rawList;
    return {
      total: all.length,
      // Đếm theo trạng thái hiệu lực (suy diễn EXPIRED khi quá end_date) — bug #110.
      active: all.filter((p) => effectiveStatus(p) === 'ACTIVE').length,
      expired: all.filter((p) => effectiveStatus(p) === 'EXPIRED').length,
      percentage: all.filter((p) => resolveDiscountType(p) === 'PERCENTAGE').length,
      fixedAmount: all.filter((p) => resolveDiscountType(p) === 'FIXED_AMOUNT').length,
    };
  }, [rawList]);

  const activeRaw = useMemo(
    () => rawList.find((r) => r.id === activeId) ?? null,
    [rawList, activeId]
  );

  // Fetch full detail for edit mode
  const { data: activeDetailData } = usePromotion(activeId ?? '', {
    enabled: !!activeId && modalMode === 'edit',
  });

  const activeDetail = useMemo<PromotionRaw | null>(() => {
    if (!activeDetailData) return null;
    const d = activeDetailData as any;
    return (d?.data ?? d) as PromotionRaw;
  }, [activeDetailData]);

  const formInitialData = useMemo(() => {
    if (modalMode === 'create') return emptyPromotionForm;
    if (modalMode === 'edit') {
      const source = activeDetail ?? activeRaw;
      if (source) return toFormData(source);
    }
    return emptyPromotionForm;
  }, [modalMode, activeDetail, activeRaw]);

  const closeModal = useCallback(() => {
    setModalMode(null);
    setActiveId(null);
  }, []);

  const handleView = useCallback((item: PromotionTableItem) => {
    setActiveId(item.id);
    setModalMode('view');
  }, []);

  const handleEdit = useCallback((item: PromotionTableItem) => {
    setActiveId(item.id);
    setModalMode('edit');
  }, []);

  const handleDelete = useCallback((item: PromotionTableItem) => {
    setActiveId(item.id);
    setModalMode('delete');
  }, []);

  const handleFormSubmit = useCallback(
    async (data: PromotionFormData) => {
      const product_ids = data.product_ids.map((t) => t.id);
      const category_ids = data.category_ids.map((t) => t.id);
      const store_ids = data.store_ids.map((t) => t.id);
      const start_at = data.start_at ? new Date(data.start_at).toISOString() : '';
      const end_at = data.end_at ? new Date(data.end_at).toISOString() : '';

      // Bug #132 đã khép (BE trả lời 19/08/2026): chỉ có `start_at`/`end_at`,
      // cả chiều đọc lẫn chiều ghi, và cả hai là `DateTimeField` nên chuỗi ISO
      // kèm timezone là đúng. Nhánh đọc `*_date` đã gỡ khỏi toàn bộ màn này.
      //
      // `PUT` là full update: LUÔN gửi cả hai, thiếu là 400 `This field is
      // required` chứ không rơi về mặc định.
      const dateFields = { start_at, end_at };

      // Bug #140: ô trống = promotion không dùng điểm.
      // - EDIT (PUT ghi đè toàn bộ): gửi `null` để XOÁ giá trị cũ — bỏ hẳn field
      //   thì không bao giờ xoá được số điểm đã đặt nhầm; gửi `0` là nói "đổi
      //   được với 0 điểm", nghĩa khác hẳn.
      // - CREATE: KHÔNG gửi field khi trống (reviewer bắt) — promotion mới chưa
      //   có gì để xoá, mà serializer `required=False` không kèm `allow_null`
      //   sẽ trả 400 "This field may not be null", hỏng luôn việc tạo promotion
      //   thường không dùng điểm.
      const trimmedPoints = data.points_cost.trim();
      const points_cost = trimmedPoints === '' ? null : Number(trimmedPoints);
      const createPointsField = points_cost == null ? {} : { points_cost };

      // `total_quantity` theo ĐÚNG quy ước của `points_cost` ở trên: EDIT gửi
      // `null` để gỡ trần đã đặt (bỏ field thì không bao giờ gỡ được), CREATE
      // thì bỏ hẳn field khi trống để tránh 400 "may not be null".
      //
      // Tên field là `total_quantity` (bug #251) — `usage_limit` không tồn tại
      // ở serializer nào, gửi lên chỉ bị nuốt im lặng.
      const trimmedLimit = data.total_quantity.trim();
      const total_quantity = trimmedLimit === '' ? null : Number(trimmedLimit);
      const createLimitField = total_quantity == null ? {} : { total_quantity };

      /**
       * Ô "Conditions" là một input chữ tự do, còn cột `promotions.conditions`
       * bên BE là **JSONB** — nên phải quy về object trước khi gửi.
       *
       * Ba dạng người dùng hay gõ, theo thứ tự thử:
       *   1. JSON thật:        {"min_order": 100000}
       *   2. key: value:       min order: 100000, max uses: 5
       *   3. câu chữ thường:   Chỉ áp dụng cho đơn tại quầy
       *
       * Bug #150: nhánh (1) trước đây gán thẳng kết quả `JSON.parse`, mà
       * `JSON.parse('100')` ra **số** chứ không phải object — gửi một số vào
       * field JSONB thì BE từ chối (hoặc ghi ra thứ không đọc lại được). Nay chỉ
       * nhận kết quả khi nó thật sự là object thuần, còn lại rơi xuống (2)/(3).
       */
      const toPlainObject = (value: unknown): PromotionConditions | null =>
        typeof value === 'object' && value !== null && !Array.isArray(value)
          ? (value as PromotionConditions)
          : null;

      let parsedConditions: PromotionConditions = {};
      const trimmedCond = data.conditions.trim();
      if (trimmedCond) {
        let parsed: PromotionConditions | null = null;

        // 1. JSON thật
        try {
          parsed = toPlainObject(JSON.parse(trimmedCond));
        } catch {
          parsed = null;
        }

        // 2. Dạng `key: value` (nhiều cặp ngăn bởi dấu phẩy)
        if (!parsed && trimmedCond.includes(':')) {
          const collected: PromotionConditions = {};
          trimmedCond.split(',').forEach((pair) => {
            const [k, ...rest] = pair.split(':');
            const key = k?.trim();
            const value = rest.join(':').trim();
            if (!key || !value) return;
            const num = Number(value);
            collected[key.toLowerCase().replace(/\s+/g, '_')] =
              value !== '' && Number.isFinite(num) ? num : value;
          });
          if (Object.keys(collected).length > 0) parsed = collected;
        }

        // 3. Câu chữ thường -> bọc vào một khoá để vẫn là object hợp lệ.
        parsedConditions = parsed ?? { description: trimmedCond };
      }

      /**
       * ⚠️ CHỜ BACKEND (bug #150) — `conditions` CÓ trong body của cả hai
       * payload dưới đây, đã kiểm bằng tay: admin gửi đúng object JSON.
       * Nếu app khách vẫn không thấy điều kiện thì mắt xích thiếu nằm ở
       * serializer promotion của BE. Chi tiết endpoint/field: xem
       * `PromotionConditions` trong `packages/shared-types/src/promotion.types.ts`.
       */
      if (modalMode === 'create') {
        const payload = {
          name: data.name.trim(),
          code: data.code.trim(),
          type: data.type,
          value: data.value.trim(),
          conditions: parsedConditions,
          ...dateFields,
          status: data.status,
          ...createPointsField,
          ...createLimitField,
          product_ids: product_ids,
          category_ids: category_ids,
          store_ids: store_ids,
        };
        await createPromotion.mutateAsync(payload as never);
      } else if (modalMode === 'edit' && activeId) {
        const payload = {
          name: data.name.trim(),
          code: data.code.trim(),
          type: data.type,
          value: data.value.trim(),
          conditions: parsedConditions,
          ...dateFields,
          status: data.status,
          points_cost,
          total_quantity,
          // Bug #131: payload sửa trước đây bỏ hẳn 3 field phạm vi. Vì service
          // dùng PUT (ghi đè toàn bộ), thiếu chúng nghĩa là mọi thay đổi
          // Products/Categories/Stores đều không bao giờ tới được BE.
          product_ids: product_ids,
          category_ids: category_ids,
          store_ids: store_ids,
        };
        await updatePromotion.mutateAsync({ id: activeId, data: payload as never });
      }
      closeModal();
    },
    [modalMode, activeId, createPromotion, updatePromotion, closeModal]
  );

  const handleConfirmDelete = useCallback(async () => {
    if (!activeId) return;
    await deletePromotion.mutateAsync(activeId);
    closeModal();
  }, [activeId, deletePromotion, closeModal]);

  const { triggerExport, isExporting } = useExport({
    endpoint: '/promotions/',
    params: selectedStoreId ? { store: selectedStoreId } : {},
    filePrefix: 'promotions',
  });

  return (
    <PageContainer>
      <PageHeader
        title={t('promotions')}
        subtitle={t('manageCouponsDiscountsAndPromotiona')}
        actions={
          <div className="flex items-center gap-2">
            <ExportButton onExport={triggerExport} isLoading={isExporting} />
            {/* Thử engine giảm giá trên giỏ mẫu (mục #41) — POST /orders/verify/ */}
            <ActionButton variant="secondary" onClick={() => setShowTester(true)}>
              {t('promoTesterTitle')}
            </ActionButton>
            <ActionButton
              variant="primary"
              icon={<PlusIcon size={16} />}
              onClick={() => setModalMode('create')}
            >
              {t('addPromotion') || 'Add Promotion'}
            </ActionButton>
          </div>
        }
      />

      {error && (
        <div className="mt-4 rounded-lg bg-red-50 p-4 text-sm text-red-700">
          <p className="font-medium">{t('failedToLoadPromotions')}</p>
        </div>
      )}

      <div className="mt-6">
        <PromotionStatsRow stats={stats} />
      </div>

      <div className="mt-6">
        <PromotionFilterBar
          searchQuery={searchQuery}
          statusFilter={statusFilter}
          onSearchChange={setSearchQuery}
          onStatusFilterChange={setStatusFilter}
        />
      </div>

      <div className="mt-4">
        <PromotionTable
          data={filteredData}
          isLoading={isLoading}
          onView={handleView}
          onEdit={handleEdit}
          onDelete={handleDelete}
          currentPage={currentPage}
          totalPages={totalPages}
          totalItems={totalItems}
          pageSize={pageSize}
          onPageChange={setCurrentPage}
        />
      </div>

      {modalMode === 'view' && activeId && (
        <PromotionDetailModal id={activeId} onClose={closeModal} />
      )}

      {/* Thử engine giảm giá (mục #41) */}
      {showTester && (
        <PromotionTesterModal
          defaultStoreId={selectedStoreId || undefined}
          onClose={() => setShowTester(false)}
        />
      )}

      {(modalMode === 'create' || modalMode === 'edit') && (
        <PromotionFormModal
          key={`${modalMode}-${activeId ?? 'new'}-${activeDetail ? 'loaded' : 'pending'}`}
          mode={modalMode}
          initialData={formInitialData}
          isSubmitting={createPromotion.isPending || updatePromotion.isPending}
          onSubmit={handleFormSubmit}
          onClose={closeModal}
        />
      )}

      {modalMode === 'delete' && (
        <PromotionDeleteModal
          isDeleting={deletePromotion.isPending}
          onConfirm={handleConfirmDelete}
          onClose={closeModal}
        />
      )}
    </PageContainer>
  );
}
